<template>
  <div class="facebook-login-container">
      <!-- 非alleat域名直接显示webview -->
      <div v-if="shouldUseWebviewLogin" class="webview-container">
        <!-- 骨架屏加载状态 -->
        <div v-if="!webviewLoaded" class="webview-skeleton">
          <div class="skeleton-content">
            <div class="skeleton-icon"></div>
            <div class="skeleton-text"></div>
          </div>
        </div>
        <!-- webview iframe -->
        <iframe 
          v-show="webviewLoaded"
          :src="webviewUrl"
          class="webview-iframe"
          frameborder="0"
          @load="onWebviewLoad"
          @error="onWebviewError"
        ></iframe>
      </div>
    
    <!-- alleat域名显示按钮 - 非安卓设备 -->
    <button 
      v-else
      class="facebook-login-btn" 
      @click="handleFacebookLogin"
      :disabled="loading"
    >
      <i v-if="loading" class="el-icon-loading"></i>
      <img v-else src="@/assets/icon/facebook.png" alt="Facebook" class="facebook-icon">
      <span>{{ loading ? 'Signing in...' : 'Continue with Facebook' }}</span>
    </button>
  </div>
</template>

<script>
import { postGateway } from '@/request';
import bus from '@/utils/bus.js';
import { shouldUseWebviewLogin } from '@/utils/domain-helper.js';
import { FACEBOOK_CONFIG, FACEBOOK_FIELDS } from '@/utils/facebook.js';
import { getWebUrl } from '@/config';

export default {
  name: 'FacebookLogin',
  props: {
    fromPage: {
      type: String,
      default: null
    },
  },
  data() {
    return {
      loading: false,
      webviewUrl: '',
      webviewLoaded: false
    }
  },
  computed: {
    shouldUseWebviewLogin() {
      return shouldUseWebviewLogin();
    },
    isAndroid() {
      return /Android/i.test(navigator.userAgent);
    }
  },
  mounted() {
    // 如果是webview模式，准备webview URL
    if (this.shouldUseWebviewLogin) {
      this.setupWebview();
      // 监听来自webview的消息
      window.addEventListener('message', this.handleWebviewMessage);
    }
    // 初始化Facebook SDK
    this.initFacebookSDK();
  },
  beforeDestroy() {
    // 清理事件监听器
    if (this.shouldUseWebviewLogin) {
      window.removeEventListener('message', this.handleWebviewMessage);
    }
  },
  methods: {
    getWebUrl,
    async handleFacebookLogin() {
      
      this.loading = true;
      
      try {
        // 使用Facebook SDK进行登录
        await this.loginWithFacebookSDK();
      } catch (error) {
        console.error('Facebook login failed:', error);
        this.handleLoginError(error);
        this.loading = false;
      }
    },

    // 初始化Facebook SDK
    initFacebookSDK() {
      // 检查Facebook SDK是否已加载
      if (window.FB) {
        this.setupFacebookLogin();
        return;
      }

      // 动态加载Facebook SDK - 使用最新版本 v24.0
      const script = document.createElement('script');
      script.src = 'https://connect.facebook.net/en_US/sdk.js';
      script.async = true;
      script.defer = true;
      script.crossOrigin = 'anonymous';
      
      script.onload = () => {
        this.setupFacebookLogin();
      };
      
      document.head.appendChild(script);
    },

    // 设置Facebook登录
    setupFacebookLogin() {
      window.FB.init({
        appId: FACEBOOK_CONFIG.APP_ID,
        cookie: true,
        xfbml: true,
        version: FACEBOOK_CONFIG.VERSION
      });

      // 设置登录状态检查
      window.FB.getLoginStatus((response) => {
        console.log('Facebook login status:', response);
      });
    },

    // 使用Facebook SDK登录
    loginWithFacebookSDK() {
      return new Promise((resolve, reject) => {
        window.FB.login((response) => {
          if (response.authResponse) {
            console.log('Facebook login successful:', response);
            this.handleFacebookResponse(response);
            resolve(response);
          } else {
            this.loading = false;
            console.log('Facebook login cancelled or failed');
            reject(new Error('Facebook login was cancelled'));
          }
        }, {
          scope: FACEBOOK_CONFIG.SCOPE,
          return_scopes: FACEBOOK_CONFIG.RETURN_SCOPES
        });
      });
    },

    // 处理Facebook登录响应
    async handleFacebookResponse(response) {
      try {
        const { accessToken, userID } = response.authResponse;
        
        // 获取用户信息 - 传递accessToken参数避免全局覆盖
        const userInfo = await this.getFacebookUserInfo(accessToken);

        console.log(userInfo,'userInfo')

        if(!userInfo.email) {
          this.$message.error('Facebook login failed. Please try others.');
          this.loading = false;
          return;
        }
        
        // 发送到后端验证 - 保持loading状态
        const loginData = await this.sendUserToBackend({
          uid: userID,
          email: userInfo.email,
          displayName: userInfo.name,
          photoURL: userInfo.picture?.data?.url || '',
          accessToken,
          provider: 'facebook'
        });

        // 处理登录成功
        this.handleLoginSuccess(loginData);
        
      } catch (error) {
        console.error('Facebook login processing error:', error);
        this.loading = false; // 确保在错误时停止loading
        throw error;
      }
    },

    // 获取Facebook用户信息
    getFacebookUserInfo(accessToken) {
      return new Promise((resolve, reject) => {
        // 使用accessToken参数而不是全局设置，避免警告
        window.FB.api('/me', {
          fields: FACEBOOK_FIELDS,
          access_token: accessToken
        }, (response) => {
          if (response && !response.error) {
            resolve(response);
          } else {
            reject(new Error(response.error?.message || 'Failed to get user info'));
          }
        });
      });
    },


    // 处理登录错误
    handleLoginError(error) {
      let errorMessage = 'Facebook login failed. Please try again.';
      
      if (error.code === 'auth/account-exists-with-different-credential') {
        errorMessage = 'An account already exists with this email using a different login method.';
        this.$emit('needAlternativeLogin');
      } else if (error.code === 'auth/popup-closed-by-user') {
        errorMessage = 'Login was cancelled. Please try again.';
      } else if (error.code === 'auth/popup-blocked') {
        errorMessage = 'Popup was blocked. Please allow popups and try again.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      this.$message.error(errorMessage);
    },
    
    async sendUserToBackend(userData) {
      try {
        console.log('Sending user data to backend...', userData);
        
        const response = await postGateway({
          url: '/oauth/appGoogleLogin',
          method: 'POST',
          data: {
            uid: userData.uid,
            accessToken: userData.accessToken,
            email: userData.email,
            firstName: userData.displayName,
            photoUrl: userData.photoURL,
            facebookId: userData.uid, // Facebook SDK返回的userID
            render: 'facebook',
            type: 'web'
          }
        });
        
        if (response.code === 200) {
          // 返回登录数据，让调用方处理
          return response.data;
        } else {
          this.loading = false; // 后端验证失败时停止loading
          this.$message.error(response.msg || 'Login failed');
          throw new Error(response.msg || 'Login failed');
        }
      } catch (error) {
        console.error('Backend verification error:', error);
        this.loading = false; // 网络错误时停止loading
        this.$message.error('Server verification failed');
        throw error;
      }
    },
    
    // 处理登录成功
    async handleLoginSuccess(loginData) {
      console.log('Login success data:', loginData);
      
      // 停止loading状态
      this.loading = false;
      
      // 检查登录数据是否有效
      if (!loginData) {
        console.error('Login data is null or undefined');
        this.$message.error('Login failed: Invalid response data');
        return;
      }
      
      // 保存登录信息 - 检查token是否存在
      if (loginData.token) {
        localStorage.setItem('token', loginData.token);
        localStorage.setItem('refreshToken', loginData.refreshToken);
        localStorage.setItem('clientId', loginData.clientId);
        localStorage.setItem('aeClient', loginData.clientId);
        localStorage.setItem('userInfo', JSON.stringify(loginData));
        
        // 通知header登录了
        bus.$emit('login');
        
        // 显示成功消息
        this.$message.success('Login successful!');
        
        // 检查是否需要绑定手机号
        const needBindPhone = await this.checkAndBindPhoneIfNeeded();
        
        // 如果不需要绑定手机号，完成登录流程
        if (!needBindPhone) {
          this.completeLogin();
        }
      } else {
        console.warn('No token in login data:', loginData);
        // 即使没有token，也尝试完成登录流程
        this.$message.success('Login successful!');
        this.completeLogin();
      }
    },
    
    // 完成登录流程
    completeLogin() {
      if (this.fromPage === 'checkout') {
        this.$emit('loginDone', true);
      } else if (this.fromPage === 'login') {
        this.$emit('loginDone', true);
      } else {
        // 默认跳转到首页
        this.$router.push('/');
      }
    },
    
    async checkAndBindPhoneIfNeeded() {
      try {
        // 获取用户详细信息
        const userDetail = await this.fetchUserDetail();
        
        if (!userDetail.contactPhone || userDetail.contactPhone === '') {
          // 需要绑定手机号
          if (this.fromPage === 'checkout') {
            // 在checkout页面，发出需要绑定手机号的事件
            this.$emit('needBindPhone');
          } else {
            // 其他页面，跳转到绑定手机号页面
            this.$router.replace('/bind-phone?fromPage=' + (this.fromPage || 'login'));
          }
          return true;
        } else {
          // 手机号已绑定
          return false;
        }
      } catch (error) {
        console.error('Check phone binding error:', error);
        return false;
      }
    },
    
    async fetchUserDetail() {
      try {
        const clientId = localStorage.getItem('clientId');
        if (!clientId) {
          return {};
        }
        const response = await postGateway({
          url: '/customerApp/center/getDetail',
          method: 'GET',
          data: {
            clientId
          }
        });
        
        if (response.code === 200) {
          return response.data;
        } else {
          throw new Error(response.msg || 'Failed to fetch user details');
        }
      } catch (error) {
        console.error('Fetch user detail error:', error);
        throw error;
      }
    },
    
    // 获取网站基础 URL
    getWebBaseUrl() {
      return this.getWebUrl();
    },
    // 设置webview
    setupWebview() {
      const baseUrl = this.getWebBaseUrl();
      const isMobile = window.innerWidth < 950;
      this.webviewUrl = `${baseUrl}/firebase-login-webview?type=facebook&embedded=true&mobile=${isMobile}`;
    },
    
    
    // webview加载完成
    onWebviewLoad() {
      console.log('Facebook login webview loaded');
    },
    
    // webview加载错误
    onWebviewError() {
      console.error('Facebook login webview failed to load');
      this.$message.error('Failed to load Facebook login. Please try again.');
    },
    
    // 处理来自webview的消息
    handleWebviewMessage(event) {
      // 允许跨域通信，但验证消息格式
      if (!event.data || typeof event.data !== 'object') {
        return;
      }
      
      // 处理webview加载完成消息
      if (event.data.type === 'WEBVIEW_LOADED' && event.data.loginType === 'facebook') {
        console.log('Facebook webview loaded successfully');
        this.webviewLoaded = true;
        return;
      }
      
      // 只处理Facebook登录相关的消息
      if (event.data.type === 'LOGIN_SUCCESS' && event.data.loginType === 'facebook') {
        console.log('Facebook login success from webview:', event.data);
        this.loading = false;
        this.handleLoginSuccess(event.data.data);
      } else if (event.data.type === 'LOGIN_ERROR' && event.data.loginType === 'facebook') {
        console.error('Facebook login error from webview:', event.data.error);
        this.loading = false;
        this.$message.error(event.data.error || 'Facebook login failed');
      }
    }
  }
}
</script>

<style scoped>
.facebook-login-container {
  width: 100%;
  margin-bottom: 16px;
  display: flex;
  justify-content: center;
  border-radius: 8px;
  overflow: hidden;
}

.facebook-login-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 300px;
  height: 48px;
  background: #1877F2;
  border: 1px solid #1877F2;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #fff;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  
  &:hover:not(:disabled) {
    background: #166FE5;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
    transform: translateY(-1px);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
}

.facebook-icon {
  width: 20px;
  height: 20px;
  margin-right: 10px;
}

.facebook-login-btn .el-icon-loading {
  animation: spin 1s linear infinite;
  margin-right: 10px;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@keyframes skeleton-pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.4;
  }
  100% {
    opacity: 1;
  }
}

/* Webview样式 */
.webview-container {
  width: 100%;
  height: 56px; /* 与按钮高度一致 */
  overflow: hidden;
  position: relative;
  
  .webview-iframe {
    width: 100%;
    height: 100%;
    border: none;
    overflow: hidden;
  }
  
  .webview-skeleton {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f5f5f5;
    border-radius: 8px;
    
    .skeleton-content {
      display: flex;
      align-items: center;
      width: 100%;
      max-width: 300px;
      padding: 0 16px;
      
      .skeleton-icon {
        width: 20px;
        height: 20px;
        background: #e0e0e0;
        border-radius: 4px;
        margin-right: 12px;
        animation: skeleton-pulse 1.5s ease-in-out infinite;
      }
      
      .skeleton-text {
        flex: 1;
        height: 16px;
        background: #e0e0e0;
        border-radius: 4px;
        animation: skeleton-pulse 1.5s ease-in-out infinite;
      }
    }
  }
}

/* 移动端适配 */
@media (max-width: 768px) {
  .facebook-login-btn {
    height: 44px;
    font-size: 13px;
    
    .facebook-icon {
      width: 18px;
      height: 18px;
      margin-right: 10px;
    }
  }
  
  .webview-container {
    height: 44px; /* 移动端与按钮高度一致 */
    
    .webview-skeleton {
      .skeleton-content {
        .skeleton-icon {
          width: 18px;
          height: 18px;
        }
        
        .skeleton-text {
          height: 14px;
        }
      }
    }
  }
}
</style>
